// Branding del sidebar: si el operador del usuario tiene el co-branding activo,
// el menú lateral muestra SOLO su logo en vez del de Beartrack.
//
// Antes esto era una preferencia en el localStorage de cada navegador
// (utils/coBranding.js, eliminado): no se propagaba a nadie y era invisible para
// los comercios, que ni siquiera tienen `logisticCompanyId` en el JWT. Ahora la
// decisión es del OPERADOR, se persiste en su empresa y la resuelve el backend.
//
// El cache es de MÓDULO y guarda la promesa, no el valor, por dos razones:
//   1. CollapsibleSidebar se monta dos veces a la vez (columna desktop + drawer
//      móvil): guardar la promesa deduplica esa doble request.
//   2. Cruzar /home <-> /admin desmonta y remonta el árbol de rutas entero, así
//      que un cache en estado de React no sobreviviría a la navegación.
// Mismo patrón que hooks/useOperatorCoverage y context/CountryContext.
import { useEffect, useState } from "react";
import useAuth from "./useAuth";
import { getSidebarBranding } from "../api/contracts";

/** Marca neutra: el sidebar muestra el logo de Beartrack. */
const NEUTRAL = { coBranding: false, logo: null, operatorName: null };

// tenantKey -> Promise<branding>
const brandingPromises = new Map();

/**
 * Clave del cache. El branding depende del tenant, no del usuario: dos usuarios
 * del mismo operador (o del mismo comercio) comparten respuesta.
 *
 * Nunca se cachea bajo una clave vacía: sin tenant no hay request que hacer.
 */
function tenantKeyOf(user) {
  const logistic = user?.logisticCompanyId?._id ?? user?.logisticCompanyId;
  if (logistic) return `logistic:${logistic}`;
  const company = user?.companyId?._id ?? user?.companyId;
  if (company) return `company:${company}`;
  return "";
}

function loadBranding(tenantKey) {
  if (!brandingPromises.has(tenantKey)) {
    brandingPromises.set(
      tenantKey,
      getSidebarBranding()
        .then((data) => ({
          coBranding: Boolean(data?.coBranding),
          logo: data?.logo || null,
          operatorName: data?.operatorName || null,
        }))
        .catch((err) => {
          // Se limpia para que un fallo de red no deje el cache envenenado toda
          // la sesión. Ante error se cae a la marca de Beartrack: es preferible
          // mostrar de más la marca propia que dejar la cabecera vacía.
          brandingPromises.delete(tenantKey);
          console.warn("useSidebarBranding: no se pudo leer el branding", err);
          return NEUTRAL;
        }),
    );
  }
  return brandingPromises.get(tenantKey);
}

export const BRANDING_EVENT = "beartrack:sidebar-branding-changed";

/**
 * Invalida el cache. La usa la pantalla de configuración al guardar el toggle,
 * para que el cambio se vea sin recargar.
 */
export function invalidateSidebarBranding() {
  brandingPromises.clear();
  window.dispatchEvent(new CustomEvent(BRANDING_EVENT));
}

/**
 * @returns {{ coBranding: boolean, logo: string|null, operatorName: string|null }}
 *
 * Arranca en marca neutra, así que en el primer paint se ve el logo de Beartrack
 * y luego se reemplaza. No hay forma de evitarlo sin red: el JWT no lleva el
 * logo, y meterlo ahí implicaría reemitir tokens para cualquier cambio de marca.
 */
export default function useSidebarBranding() {
  const { user } = useAuth();
  const tenantKey = tenantKeyOf(user);
  const [branding, setBranding] = useState(NEUTRAL);

  useEffect(() => {
    if (!tenantKey) {
      setBranding(NEUTRAL);
      return;
    }
    let alive = true;
    const read = () => {
      loadBranding(tenantKey).then((data) => {
        if (alive) setBranding(data);
      });
    };
    read();
    // Al guardar el toggle en Configuración se limpia el cache y se emite este
    // evento; sin esto el admin no vería su propio cambio hasta recargar.
    window.addEventListener(BRANDING_EVENT, read);
    return () => {
      alive = false;
      window.removeEventListener(BRANDING_EVENT, read);
    };
  }, [tenantKey]);

  return branding;
}
